import React from 'react';
import { useIntl } from 'umi';
import Bubble from '@home/components/TeaComponents/Bubble';
import GameImage from '@home/components/GameImage';
import { getCountryFlag } from '@home/_config/countries';
import { formatKMBWithPlaceholder, formatDecimalToPercentage } from '@intelligence/v3/utils/number';
import StyledSubtitle from '../StyledSubtitle';
import IconRoblox from '@intelligence/assets/imgs/CasualWordlWide/icon-roblox.png';
import Rank01 from '@intelligence/assets/imgs/CasualWordlWide/rank01.png';
import Rank02 from '@intelligence/assets/imgs/CasualWordlWide/rank02.png';
import Rank03 from '@intelligence/assets/imgs/CasualWordlWide/rank03.png';
import './index.scss';

/** roblox.top_list[] 接口字段 */
interface RobloxTopListItem {
  game_id: string;
  edition_id: string;
  game_name: string;
  cover: string;
  genre: string;
  sub_genre: string;
  publisher: string;
  publisher_country_abbre: string;
  roblox__ccu_rank: number | null;
  roblox__peak_ccu: number | null;
  roblox__peak_ccu_growth: number | null;
  roblox__peak_ccu_ratio: number | null;
  roblox__ccu_rank_growth: number | null;
  release_time: string;
  is_new_online?: boolean;
  change_type?: string;
  gameplay_tags?: string[];
  gameplay_tags_en?: string[];
  social_tags?: string[];
  social_tags_en?: string[];
  theme_tags?: string[];
  theme_tags_en?: string[];
  is_rng?: string;
}

type TRobloxTagVariant = 'gameplay' | 'social' | 'theme' | 'rng';

/** roblox 接口字段 */
export interface RobloxData {
  top_list?: RobloxTopListItem[];
}

interface IRobloxBreakoutsProps {
  data: RobloxData | null;
  reportDate?: string;
  locale?: string;
  genreOptionsMap?: IRecord<any[]>;
}


/** 计算上线天数：reportDate - release_time */
const calcReleaseDays = (releaseTime: string, reportDate?: string): string => {
  if (!releaseTime) return '-';
  const releaseDate = new Date(releaseTime);
  const baseDate = reportDate ? new Date(reportDate) : new Date();
  const diffMs = baseDate.getTime() - releaseDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return '-';
  return `${diffDays}`;
};

const getLocalizedTags = (
  zhTags: string[] | undefined,
  enTags: string[] | undefined,
  isZh: boolean,
): string[] => {
  const tags = isZh ? zhTags : enTags;
  return tags?.filter(Boolean) || [];
};

const RobloxBreakouts: React.FC<IRobloxBreakoutsProps> = ({ data, reportDate, locale }) => {
  const { formatMessage } = useIntl();
  const isZh = locale === 'zh-CN';

  if (!data) {
    return null;
  }

  const top_list = data?.top_list ?? [];

  const renderTagColumnWrapper = (content: React.ReactNode) => (
    <div className="dbi-worldwide-trend-category">
      <div className="dbi-worldwide-game-tags">{content}</div>
    </div>
  );

  const renderTagColumn = (tags: string[] | undefined, variant: TRobloxTagVariant) => {
    const validTags = tags?.filter(Boolean) ?? [];

    if (!validTags.length) {
      return renderTagColumnWrapper(
        <span className="dbi-worldwide-trend-nums">TBD</span>,
      );
    }

    return renderTagColumnWrapper(
      validTags.map((tag, idx) => (
        <Bubble key={`${variant}-${idx}-${tag}`} content={tag ?? ''}>
          <div
            className={`dbi-worldwide-roblox-tag dbi-worldwide-roblox-tag--${variant}${
              variant !== 'rng' ? ' dbi-worldwide-roblox-tag--truncate' : ''
            }`}
          >
            {tag ?? ''}
          </div>
        </Bubble>
      )),
    );
  };

  const renderRngColumn = (isRng?: string) => {
    if (isRng === undefined || isRng === null || isRng === '') {
      return renderTagColumn(undefined, 'rng');
    }

    if (isRng === '0') {
      return renderTagColumnWrapper(
        <span className="dbi-worldwide-trend-nums">-</span>,
      );
    }

    return renderTagColumn(['RNG'], 'rng');
  };

  const renderRankingTable = () => (
    <div className="dbi-worldwide-panel dbi-worldwide-hot-roblox">
      <div className="dbi-worldwide-ranking__table">
        <div className="dbi-worldwide-ranking__table-header">
          <div className="dbi-worldwide-ranking__table-th">
            <div className="dbi-worldwide-ranking__table-th-title">
              <span><img src={IconRoblox} alt="" /></span>
              <span>{formatMessage({ id: 'dbi_worldwide_roblox_hot_games' })}</span>
            </div>
          </div>
          <div className="dbi-worldwide-ranking__table-th">
            <div className="s1">
              {formatMessage({ id: 'dbi_worldwide_roblox_ccu_24h' })}
              <span className="dbi-worldwide-rank-arrow"></span>
            </div>
          </div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_roblox_release_days' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_roblox_gameplay' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_roblox_social' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_roblox_theme' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_roblox_rng' })}</div>
        </div>
        <div className="dbi-worldwide-ranking__table-tbody">
          {top_list.map((item, idx) => {
            const ccuGrowth = item.roblox__peak_ccu_growth;
            const isNewOnline = item.is_new_online;
            const ccuTrendType = ccuGrowth === null || ccuGrowth === 0 ? 'equal' : ccuGrowth > 0 ? 'up' : 'down';
            const ccuTrendText = formatDecimalToPercentage(item.roblox__peak_ccu_ratio, { emptyValue: '' });
            const rank = item.roblox__ccu_rank ?? idx + 1;

            return (
              <div className="dbi-worldwide-ranking__table-row" key={item.game_id || idx}>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-ranking-figure">
                    <div className="dbi-worldwide-ranking-index">
                      {rank <= 3
                        ? <img src={rank === 1 ? Rank01 : rank === 2 ? Rank02 : Rank03} alt="" className="dbi-worldwide-ranking-index-img" />
                        : rank}
                    </div>
                    <div className="dbi-worldwide-ranking-figure-content">
                      <div className="dbi-worldwide-ranking-figure-avatar">
                        <GameImage entityType="pc" src={item.cover} alt={item.game_name} />
                      </div>
                      <div className="dbi-worldwide-ranking-figure-info">
                        <div className="dbi-worldwide-ranking-figure-title">
                          <Bubble content={item.game_name}>
                            <span className="dbi-worldwide-ranking-figure-name">{item.game_name}</span>
                          </Bubble>
                          {item.change_type ? (
                            item.change_type === '突增' ? (
                              <span className="dbi-worldwide-ranking-tag --surge">{formatMessage({ id: 'dbi_worldwide_tag_surge' })}</span>
                            ) : (
                              <span className="dbi-worldwide-ranking-tag --new">{formatMessage({ id: 'dbi_worldwide_tag_new' })}</span>
                            )
                          ) : null}
                        </div>
                        <div className="dbi-worldwide-ranking-figure-desc">
                          {item.publisher_country_abbre && (
                            <img className="dbi-worldwide-ranking-figure-desc-img" src={getCountryFlag(item.publisher_country_abbre)} alt="" />
                          )}
                          <Bubble content={item.publisher}>
                            <span className="dbi-worldwide-ranking-figure-desc-text">{item.publisher}</span>
                          </Bubble>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-text">
                    <div className="dbi-worldwide-trend-nums">{formatKMBWithPlaceholder(item.roblox__peak_ccu)}</div>
                    {isNewOnline ? (
                      <span className="dbi-worldwide-game-income-trend --up" style={{ color: '#009444' }}>{formatMessage({ id: 'dbi_worldwide_new_entry' })}</span>
                    ) : ccuTrendType === 'equal' ? (
                      <span className="dbi-worldwide-game-income-trend --equal"></span>
                    ) : (
                      <span className={`dbi-worldwide-game-income-trend --${ccuTrendType}`}>{ccuTrendType === 'up' && ccuTrendText ? `+${ccuTrendText}` : ccuTrendText}</span>
                    )}
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-text">
                    <div className="dbi-worldwide-trend-nums">{calcReleaseDays(item.release_time, reportDate)}{calcReleaseDays(item.release_time, reportDate) !== '-' ? ` ${formatMessage({ id: 'dbi_worldwide_days_unit' })}` : ''}</div>
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  {renderTagColumn(
                    getLocalizedTags(item.gameplay_tags, item.gameplay_tags_en, isZh),
                    'gameplay',
                  )}
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  {renderTagColumn(
                    getLocalizedTags(item.social_tags, item.social_tags_en, isZh),
                    'social',
                  )}
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  {renderTagColumn(
                    getLocalizedTags(item.theme_tags, item.theme_tags_en, isZh),
                    'theme',
                  )}
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  {renderRngColumn(item.is_rng)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="dbi-worldwide-roblox-ccu-source">{formatMessage({ id: 'dbi_worldwide_roblox_ccu_source' })}</div>
      </div>
    </div>
  );

  return (
    <div className="dbi-worldwide-tabcontent">
      <div className="dbi-worldwide-title-wrap">
        <StyledSubtitle text={formatMessage({ id: 'dbi_worldwide_roblox_breakouts' })} />
        <div className="dbi-worldwide-tips">
          <div className="dbi-worldwide-tips-item">
            <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_new_label' })}</b>
            {formatMessage({ id: 'dbi_worldwide_roblox_tip_new_desc' })}
          </div>
          <div className="dbi-worldwide-tips-item">
            <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_surge_label' })}</b>
            {formatMessage({ id: 'dbi_worldwide_roblox_tip_surge_desc' })}
          </div>
        </div>
      </div>

      {top_list?.length > 0 && renderRankingTable()}
    </div>
  );
};

export default RobloxBreakouts;
